<template>
	<TransitionRoot
		v-if="!docked"
		:show="isOpen"
		as="template"
	>
		<Dialog
			as="div"
			class="relative z-[9998]"
			@close="handleOutsideClick"
		>
			<TransitionChild
				v-if="!hideBackdrop"
				as="template"
				enter="ease-in-out duration-200"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="ease-in-out duration-200"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 z-40 transition-opacity bg-black/60" />
			</TransitionChild>
			<div :class="containerClasses">
				<TransitionChild
					as="div"
					enter="duration-300 ease-out"
					enter-from="opacity-0 scale-95"
					enter-to="opacity-100 scale-100"
					leave="duration-200 ease-in"
					leave-from="opacity-100 scale-100"
					leave-to="opacity-0 scale-95"
					:class="panelWrapperClasses"
				>
					<slot
						name="panel"
						:close-modal="closeModal"
						:emit-cancel="emitCancel"
						:emit-confirm="emitConfirm"
						:max-height="maxHeight"
					>
						<DialogPanel
							:class="panelClasses"
						>
							<slot
								name="close-button"
								:emit-cancel="emitCancel"
							>
								<button
									v-if="!hideClose"
									type="button"
									class="absolute top-0 right-0 p-2 m-0.5 text-mx-gray-400 rounded-full hover:text-mx-gray-500 hover:bg-mx-gray-100 dark:hover:bg-mx-green-600 dark:hover:text-mx-gray-300 focus:outline-none"
									@click="emitCancel"
								>
									<span class="sr-only">
										Close
									</span>
									<svg
										class="w-3 h-3"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										aria-hidden="true"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</slot>
							<div :class="maxHeight">
								<slot
									name="default"
									:close-modal="closeModal"
									:emit-cancel="emitCancel"
									:emit-confirm="emitConfirm"
								>
									<component
										:is="titleElement"
										v-if="!hideTitle"
										:class="titleClasses"
									>
										<slot name="title">
											<span class="">
												{{ title }}
											</span>
										</slot>
									</component>
									<div
										:class="[
											bodyMaxHeight,
											'px-4 overflow-x-hidden overflow-y-auto'
										]"
									>
										<slot
											name="body"
											:close-modal="closeModal"
											:emit-cancel="emitCancel"
											:emit-confirm="emitConfirm"
										>
											<div class="flex flex-col gap-4">
												<div :class="descriptionClasses">
													{{ description }}
												</div>
											</div>
										</slot>
									</div>
									<slot
										v-if="!hideFooter"
										name="footer"
										:close-modal="closeModal"
										:emit-cancel="emitCancel"
										:emit-confirm="emitConfirm"
									>
										<div class="flex flex-row items-center justify-end gap-4 px-4 pt-4">
											<FormButton
												v-if="!hideCancel"
												:button-width="cancelWidth"
												:button-text="cancelText"
												variant="secondary-foreground"
												:disabled="false"
												@click="emitCancel"
											/>
											<FormButton
												v-if="!hideConfirm"
												:button-text="confirmText"
												:button-width="confirmWidth"
												:variant="confirmDisabled ? 'disabled' : 'primary'"
												:is-loading="requestLoading"
												submit
												:disabled="confirmDisabled"
												@submit="emitConfirm"
											/>
										</div>
									</slot>
								</slot>
							</div>
						</DialogPanel>
					</slot>
				</TransitionChild>
			</div>
		</Dialog>
	</TransitionRoot>
	<div
		v-else-if="isOpen"
		:class="containerClasses"
	>
		<div :class="panelWrapperClasses">
			<slot
				name="panel"
				:close-modal="closeModal"
				:emit-cancel="emitCancel"
				:emit-confirm="emitConfirm"
				:max-height="maxHeight"
			>
				<div :class="panelClasses">
					<slot
						name="close-button"
						:emit-cancel="emitCancel"
					>
						<button
							v-if="!hideClose"
							type="button"
							class="absolute top-0 right-0 p-2 m-0.5 text-mx-gray-400 rounded-full hover:text-mx-gray-500 hover:bg-mx-gray-100 dark:hover:bg-mx-green-600 dark:hover:text-mx-gray-300 focus:outline-none"
							@click="emitCancel"
						>
							<span class="sr-only">
								Close
							</span>
							<svg
								class="w-3 h-3"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								aria-hidden="true"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</slot>
					<div :class="maxHeight">
						<slot
							name="default"
							:close-modal="closeModal"
							:emit-cancel="emitCancel"
							:emit-confirm="emitConfirm"
						>
							<component
								:is="titleElement"
								v-if="!hideTitle"
								:class="titleClasses"
							>
								<slot name="title">
									<span class="">
										{{ title }}
									</span>
								</slot>
							</component>
							<div
								:class="[
									bodyMaxHeight,
									'px-4 overflow-x-hidden overflow-y-auto'
								]"
							>
								<slot
									name="body"
									:close-modal="closeModal"
									:emit-cancel="emitCancel"
									:emit-confirm="emitConfirm"
								>
									<div class="flex flex-col gap-4">
										<div :class="descriptionClasses">
											{{ description }}
										</div>
									</div>
								</slot>
							</div>
							<slot
								v-if="!hideFooter"
								name="footer"
								:close-modal="closeModal"
								:emit-cancel="emitCancel"
								:emit-confirm="emitConfirm"
							>
								<div class="flex flex-row items-center justify-end gap-4 px-4 pt-4">
									<FormButton
										v-if="!hideCancel"
										:button-width="cancelWidth"
										:button-text="cancelText"
										variant="secondary-foreground"
										:disabled="false"
										@click="emitCancel"
									/>
									<FormButton
										v-if="!hideConfirm"
										:button-text="confirmText"
										:button-width="confirmWidth"
										:variant="confirmDisabled ? 'disabled' : 'primary'"
										:is-loading="requestLoading"
										submit
										:disabled="confirmDisabled"
										@submit="emitConfirm"
									/>
								</div>
							</slot>
						</slot>
					</div>
				</div>
			</slot>
		</div>
	</div>
</template>

<script setup>
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	TransitionChild,
	TransitionRoot
} from '@headlessui/vue'

const props = defineProps({
	id: {
		type: String,
		required: true
	},
	title: {
		type: String,
		default: 'Modal Title'
	},
	description: {
		type: String,
		default: ''
	},
	confirmText: {
		type: String,
		default: 'Confirm'
	},
	cancelText: {
		type: String,
		default: 'Cancel'
	},
	overrideControls: {
		type: Boolean,
		default: false
	},
	isOpen: {
		type: Boolean,
		default: true
	},
	bgColor: {
		type: String,
		default: 'bg-white dark:bg-mx-green-700'
	},
	neumorphic: {
		type: Boolean,
		default: false
	},
	hideBackdrop: {
		type: Boolean,
		default: false
	},
	docked: {
		type: Boolean,
		default: false
	},
	dockedClasses: {
		type: String,
		default: 'fixed bottom-0 left-0 z-50 flex items-end justify-start max-h-screen p-4'
	},
	minimized: {
		type: Boolean,
		default: false
	},
	titleClasses: {
		type: String,
		default: 'text-xl lg:text-3xl font-semibold px-4 dark:text-white'
	},
	descriptionClasses: {
		type: String,
		default: 'text-sm text-mx-gray-500 dark:text-mx-green-600'
	},
	confirmWidth: {
		type: String,
		default: 'w-32'
	},
	cancelWidth: {
		type: String,
		default: 'w-32'
	},
	hideFooter: {
		type: Boolean,
		default: false
	},
	hideTitle: {
		type: Boolean,
		default: false
	},
	hideCancel: {
		type: Boolean,
		default: false
	},
	hideConfirm: {
		type: Boolean,
		default: false
	},
	hideClose: {
		type: Boolean,
		default: false
	},
	maxWidth: {
		type: String,
		default: 'max-w-lg'
	},
	maxHeight: {
		type: String,
		default: 'max-h-[90vh]'
	},
	bodyMaxHeight: {
		type: String,
		default: 'max-h-[70vh]'
	},
	confirmDisabled: {
		type: Boolean,
		default: false
	},
	preventOutsideClickClose: {
		type: Boolean,
		default: false
	},
	requestLoading: {
		type: Boolean,
		default: false
	},
	buttonText: {
		type: String,
		default: 'Confirm'
	},
	disableLeftMargin: {
		type: Boolean,
		default: false
	}
})

const {
	overrideControls,
	confirmDisabled,
	preventOutsideClickClose,
	disableLeftMargin,
	hideBackdrop,
	docked,
	dockedClasses
} = toRefs(props)

const titleElement = computed(() => (docked.value ? 'div' : DialogTitle))

const containerClasses = computed(() => {
	if (docked.value) {
		return [ dockedClasses.value ]
	}
	return [
		'fixed inset-0 z-50 flex items-center justify-center w-screen max-h-screen overflow-y-auto',
		disableLeftMargin.value && !props.minimized ? 'md:ml-0' : '',
		disableLeftMargin.value && props.minimized ? '' : 'md:ml-32',
		props.minimized ? 'lg:px-0 -ml-8' : 'px-4'
	]
})

const panelWrapperClasses = computed(() => {
	if (docked.value) {
		return [ 'w-auto max-w-5xl' ]
	}
	return [ 'w-full' ]
})

const panelClasses = computed(() => {
	const classes = [
		'relative max-h-screen isolate overflow-hidden pointer-events-auto',
		props.minimized ? 'p-0 rounded-3xl' : 'px-2 py-6 rounded-xl shadow-xl dark:shadow-white/5',
		props.maxWidth,
		props.neumorphic ? 'bg-neumorphic dark:bg-neumorphic' : props.bgColor
	]
	if (!docked.value) {
		classes.push('mx-auto')
	} else {
		classes.push('ml-0 mr-auto')
		classes.push('w-full')
	}
	return classes
})

const emit = defineEmits([
	'onConfirm',
	'onCancel',
	'onClose'
])

const handleOutsideClick = () => {
	if (!preventOutsideClickClose.value) {
		emitCancel()
	}
}

const emitConfirm = () => {
	if (!confirmDisabled.value) {
		emit('onConfirm')
		if (!overrideControls.value) {
			closeModal()
		}
	}
}

const emitCancel = () => {
	emit('onCancel')
	if (!overrideControls.value) {
		closeModal()
	}
}

const closeModal = () => {
	emit('onClose')
}
</script>

<style scoped>
/* WebKit Browsers - Light Mode */
/* width */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

/* Track */
::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: #E8E8E8;
  border-radius: 10px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #C0C0C0;
}

/* Remove scrollbar buttons */
::-webkit-scrollbar-button {
  display: none;
}

/* Firefox - Light Mode */
* {
  scrollbar-width: thin;
  scrollbar-color: #E8E8E8 transparent;
}

/* WebKit Browsers - Dark Mode */
@media (prefers-color-scheme: dark) {
  ::-webkit-scrollbar-thumb {
    background: #6A6A6A;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #ADADAD;
  }
}

/* Firefox - Dark Mode */
@media (prefers-color-scheme: dark) {
  * {
    scrollbar-color: #6A6A6A transparent;
  }
}

/* Class-based Dark Mode (if using .dark class) */
.dark ::-webkit-scrollbar-thumb {
  background: #6A6A6A;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #ADADAD;
}

.dark * {
  scrollbar-color: #6A6A6A transparent;
}
</style>
